/* ============================================================
   Tab Schedule — senarai produk (kiri) + kalendar slot (kanan)

   Kedua-duanya kelihatan SERENTAK, bukan bertukar ganti. Sebabnya
   ialah tindakan utama pada skrin ini: menyeret produk masuk ke slot
   waktu tertentu. Kalau senarai dan kalendar berada di belakang suis,
   seretan itu mustahil.

   Nisbah 30/70: senarai hanya perlu muat nama produk dan tanda set,
   manakala kalendar perlu empat lajur hari berisi slot. Memberi
   senarai lebih daripada itu memakan lebar yang kalendar tidak boleh
   ganti.

   ⚠️ SUSUNAN BELUM DISIMPAN KE SERVER. Slot yang diisi hidup dalam
   ingatan (dan simpanan browser, kalau pemanggil memberikannya) —
   tiada video benar-benar dijadualkan ke TikTok lagi. Laluan sebenar
   ke TikTok memerlukan release extension baharu; lihat nota dalam
   WorkspaceView.
   ============================================================ */

import { useState, useMemo, useEffect, useRef, cloneElement } from 'react';
import { Undo2, Plus, Check, X, CalendarClock, Pencil, Trash2, AlertTriangle, RefreshCw, ChevronDown, Search, User, PanelRightOpen, PanelLeftOpen } from 'lucide-react';

import { Badge, Btn, EmptyState, Field } from '../design/ui';
import { useT } from '../i18n';
import KalendarMingguV39 from './KalendarMingguV39';
import MenuStatusPukal from './MenuStatusPukal';
import { kunciSlot, kembangIkutTemplate, slotKosongDari } from './scheduleV39';
import { useGagalDitutup } from '../hooks/useGagalDitutup';

const SUSUNAN_KOSONG = { hariTemplate: {}, isian: {} };

/* Satu baris produk dalam senarai kiri.
   `luar` = { set1: 'Akaun 1', … } — set yang sudah dijadualkan pada
   kalendar akaun LAIN, beserta nama akaunnya. */
function KadSeret({ p, jumlahSet, sudah, boleh, luar = {} }) {
    const t = useT();

    return (
        <div
            className={'s-seret' + (boleh ? '' : ' s-seret--habis')}
            draggable={boleh}
            onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', p.id);
                /* 'copyMove' — lihat nota penuh pada pemegang seret dalam
                   WorkspaceView: slot kalendar mengisytiharkan
                   `dropEffect = 'move'`, dan 'copy' sahaja menyebabkan
                   browser membatalkan jatuhan secara senyap. */
                e.dataTransfer.effectAllowed = 'copyMove';
            }}
            title={boleh
                ? t('workspace.tiktok.dragHint')
                : t('workspace.tiktok.dragExhausted')}
        >
            {/* Gambar produk SEBENAR bila ada (workspace sebenar); prototaip
                menghantar warna sahaja. Ia elemen berbeza dan bukan satu
                <span> dengan background-image kerana gambar yang gagal
                dimuat perlu jatuh balik kepada kotak kosong, bukan
                meninggalkan lubang putih. */}
            {p.gambar ? (
                <img
                    className="s-seret__gambar"
                    src={p.gambar}
                    alt=""
                    draggable={false}
                    style={{ objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
            ) : (
                <span className="s-seret__gambar" style={{ background: p.thumb }} />
            )}

            <span className="s-seret__teks">
                <span className="s-seret__nama">{p.name}</span>
                <span className="s-seret__set">
                    {['set1', 'set2', 'set3'].map((s, i) => {
                        const adaVideo = (p.videos || [])[i] === 1;
                        const dijadual = sudah.includes(s);
                        /* Ditempah di kalendar akaun lain. Dikira hanya bila ia
                           TIDAK ada dalam kalendar ini — kalau kedua-duanya
                           benar, yang di depan mata pengguna lebih penting. */
                        const diLuar = adaVideo && !dijadual && !!luar[s];
                        return (
                            <span
                                key={s}
                                className={
                                    's-seret__titik'
                                    + (adaVideo ? ' s-seret__titik--ada' : '')
                                    + (diLuar ? ' s-seret__titik--luar' : '')
                                    + (dijadual ? ' s-seret__titik--guna' : '')
                                }
                                title={
                                    !adaVideo ? t('workspace.tiktok.setNoVideo', { n: i + 1 })
                                        : dijadual ? t('workspace.tiktok.setScheduled', { n: i + 1 })
                                            : diLuar ? t('workspace.tiktok.setScheduledOnAccount', { n: i + 1, akaun: luar[s] })
                                                : t('workspace.tiktok.setReady', { n: i + 1 })
                                }
                            >
                                {i + 1}
                            </span>
                        );
                    })}
                    <span className="s-seret__kira">{sudah.length}/{jumlahSet}</span>
                </span>
            </span>
        </div>
    );
}

/* ---------- Penukar akaun ------------------------------------------
   Setiap akaun TikTok mempunyai susunan kalendarnya SENDIRI. Tanpa
   pemisahan ini, pengguna yang mengendalikan beberapa akaun akan
   melihat satu kalendar bercampur dan tiada cara untuk mengetahui
   video mana milik akaun yang mana.

   Label ditaip sendiri oleh pengguna ("Akaun Utama", "Akaun Baju")
   dan bukan diambil daripada profile browser berdaftar — webapp
   tidak tahu ia sedang berjalan pada browser yang mana, jadi label
   yang diambil automatik akan salah lebih kerap daripada betul. */
function PenukarAkaun({ senarai, aktif, onTukar, onCipta, onNama, onPadam }) {
    const t = useT();
    /* Corak SAMA dengan pemilih group (GroupSwitcherV39): satu chip yang
       membuka menu — "Tambah akaun" di kepala, carian bila akaun banyak, dan
       pensel/tong sampah muncul pada baris bila dituding sahaja. Sebelum ini
       ketiga-tiga ikon terpampang sebagai butang berasingan dalam bar;
       memindahkannya ke dalam menu mengosongkan bar (arahan user 2026-08-20).
       Fungsi cipta/nama/padam tidak berubah — cuma tempatnya. */
    const [buka, setBuka] = useState(false);
    const [cari, setCari] = useState('');
    const [modBaharu, setModBaharu] = useState(false);
    const [namaBaharu, setNamaBaharu] = useState('');
    const [suntingId, setSuntingId] = useState(null);
    const [namaSunting, setNamaSunting] = useState('');
    /* Padam dua langkah PADA BARIS: klik tong sampah → baris tunjuk "Delete?",
       klik itu sahkan. Tiada modal — pengendali di WorkspaceView sudah menjaga
       akaun terakhir + memilih semula akaun aktif bila yang dipadam itu aktif. */
    const [sahPadamId, setSahPadamId] = useState(null);
    const rujuk = useRef(null);

    const pilihan = senarai.find((a) => a.id === aktif) || senarai[0];

    const tutup = () => {
        setBuka(false); setCari(''); setModBaharu(false); setNamaBaharu('');
        setSuntingId(null); setNamaSunting(''); setSahPadamId(null);
    };

    /* Tutup bila klik di luar atau Esc. Esc membatalkan medan/pengesahan yang
       terbuka DAHULU, barulah menu — satu ketukan tidak membuang taipan DAN
       menutup menu sekali (corak sama GroupSwitcherV39). */
    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) tutup(); };
        const esc = (e) => {
            if (e.key !== 'Escape') return;
            if (modBaharu) { setModBaharu(false); setNamaBaharu(''); return; }
            if (suntingId) { setSuntingId(null); setNamaSunting(''); return; }
            if (sahPadamId) { setSahPadamId(null); return; }
            tutup();
        };
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
        };
    }, [buka, modBaharu, suntingId, sahPadamId]);

    const cipta = () => {
        const bersih = namaBaharu.trim();
        if (!bersih) return;
        onCipta?.(bersih);
        tutup();
    };
    const simpanNama = () => {
        const bersih = namaSunting.trim();
        if (!bersih) return;
        onNama?.(suntingId, bersih);
        setSuntingId(null); setNamaSunting('');
    };

    const senaraiTapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        return q ? senarai.filter((a) => a.nama.toLowerCase().includes(q)) : senarai;
    }, [senarai, cari]);

    return (
        <span ref={rujuk} style={{ position: 'relative', display: 'inline-flex' }}>
            {/* Butang trigger = gaya SELECT asal (`.s-pick`), sama seperti sebelum
                ini — bukan chip. HANYA menu di bawah yang ikut corak switch group
                (arahan user 2026-08-20). Lebar 150 = nilai `lebar` PilihV39 asal. */}
            <button
                type="button"
                className="s-pick"
                style={{ height: 32, fontSize: 13, width: 150 }}
                aria-haspopup="menu" aria-expanded={buka}
                aria-label={t('workspace.tiktok.accountSelectAria')}
                onClick={() => (buka ? tutup() : setBuka(true))}
            >
                <span className="s-pick__t" style={{ color: pilihan ? 'var(--s-ink)' : 'var(--s-ink-3)' }}>
                    {pilihan?.nama || ''}
                </span>
                <ChevronDown size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
            </button>

            {buka && (
                <div
                    className="s-pop"
                    role="menu"
                    /* Kepala menu (Tambah + carian) dikeluarkan daripada kawasan
                       tatal supaya ia kekal di tempatnya walau senarai panjang. */
                    style={{ width: 260, maxHeight: 'none', overflow: 'visible', padding: 0 }}
                >
                    <div style={{
                        padding: 5, borderBottom: '1px solid var(--s-line)',
                        display: 'flex', flexDirection: 'column', gap: 5,
                    }}>
                        {modBaharu ? (
                            <div style={{ display: 'flex', gap: 6, padding: '2px 4px' }}>
                                <input
                                    autoFocus className="s-input" value={namaBaharu} maxLength={40}
                                    placeholder={t('workspace.tiktok.accountNamePlaceholder')}
                                    aria-label={t('workspace.tiktok.accountNameAria')}
                                    onChange={(e) => setNamaBaharu(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); cipta(); } }}
                                />
                                <Btn size="sm" style={{ flex: 'none' }} onClick={cipta}>{t('workspace.tiktok.save')}</Btn>
                            </div>
                        ) : (
                            <div
                                className="s-pop__row" role="menuitem" tabIndex={0}
                                onClick={() => setModBaharu(true)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModBaharu(true); } }}
                            >
                                <Plus size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
                                <span style={{ flex: 1, fontSize: 13, color: 'var(--s-ink)' }}>{t('workspace.tiktok.addAccount')}</span>
                            </div>
                        )}

                        {!modBaharu && senarai.length >= 6 && (
                            <Field
                                icon={<Search size={14} strokeWidth={1.9} />}
                                placeholder={t('workspace.tiktok.searchAccount')}
                                value={cari}
                                onChange={(e) => setCari(e.target.value)}
                                style={{ minWidth: 0, margin: '0 4px 2px' }}
                            />
                        )}
                    </div>

                    <div style={{ maxHeight: 264, overflowY: 'auto', padding: 5 }}>
                        {senaraiTapis.length === 0 && (
                            <p className="s-hint" style={{ margin: 0, padding: '6px 9px' }}>
                                {t('workspace.tiktok.noAccountMatch')}
                            </p>
                        )}

                        {senaraiTapis.map((a) => (
                            suntingId === a.id ? (
                                <div key={a.id} style={{ display: 'flex', gap: 6, padding: '2px 4px' }}>
                                    <input
                                        autoFocus className="s-input" value={namaSunting} maxLength={40}
                                        placeholder={t('workspace.tiktok.accountNamePlaceholder')}
                                        aria-label={t('workspace.tiktok.accountNameAria')}
                                        onChange={(e) => setNamaSunting(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); simpanNama(); } }}
                                    />
                                    <Btn size="sm" style={{ flex: 'none' }} onClick={simpanNama}>{t('workspace.tiktok.save')}</Btn>
                                </div>
                            ) : (
                                <div
                                    key={a.id}
                                    className="s-pop__row" role="menuitem" tabIndex={0}
                                    onClick={() => { onTukar?.(a.id); tutup(); }}
                                    onMouseLeave={() => setSahPadamId((v) => (v === a.id ? null : v))}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTukar?.(a.id); tutup(); } }}
                                >
                                    <User size={14} strokeWidth={1.8} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
                                    <span style={{
                                        flex: 1, minWidth: 0, fontSize: 13, color: 'var(--s-ink)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {a.nama}
                                    </span>

                                    {sahPadamId === a.id ? (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); onPadam?.(a.id); tutup(); }}
                                            title={t('workspace.tiktok.deleteAccount')}
                                            style={{
                                                flex: 'none', height: 24, padding: '0 8px', borderRadius: 6, cursor: 'pointer',
                                                fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                                                color: 'var(--s-stop)', background: 'transparent', border: '1px solid var(--s-stop)',
                                            }}
                                        >
                                            {t('workspace.tiktok.deleteAccountConfirm')}
                                        </button>
                                    ) : (
                                        <>
                                            {onNama && (
                                                <span
                                                    role="button" tabIndex={0} className="s-pop__edit"
                                                    aria-label={t('workspace.tiktok.renameAccount')} title={t('workspace.tiktok.renameAccount')}
                                                    onClick={(e) => { e.stopPropagation(); setSahPadamId(null); setSuntingId(a.id); setNamaSunting(a.nama); }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSuntingId(a.id); setNamaSunting(a.nama); } }}
                                                >
                                                    <Pencil size={12} strokeWidth={2} />
                                                </span>
                                            )}
                                            {onPadam && senarai.length > 1 && (
                                                <span
                                                    role="button" tabIndex={0} className="s-pop__edit"
                                                    aria-label={t('workspace.tiktok.deleteAccount')} title={t('workspace.tiktok.deleteAccount')}
                                                    onClick={(e) => { e.stopPropagation(); setSahPadamId(a.id); }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSahPadamId(a.id); } }}
                                                >
                                                    <Trash2 size={12} strokeWidth={2} />
                                                </span>
                                            )}
                                        </>
                                    )}

                                    {a.id === aktif && <Check size={14} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}
        </span>
    );
}

export default function JadualTiktokV39({
    templates = [], kad = [], capsyen,

    /* Susunan (template setiap hari + isi setiap slot) boleh DIPEGANG DI
       LUAR. Workspace sebenar berbuat demikian supaya susunan tidak lenyap
       setiap kali pengguna menukar tab — komponen ini dilepaskan daripada
       DOM pada saat itu, jadi state dalamannya akan mati bersamanya.
       Prototaip tidak menghantar apa-apa dan kekal memegangnya sendiri. */
    susunan, onUbahSusunan,

    /* Akaun TikTok. Prototaip tidak menghantar apa-apa → penukar tidak
       muncul langsung dan seluruh skrin berkelakuan seperti dahulu. */
    akaun = null, akaunAktif = null, onTukarAkaun, onCiptaAkaun, onNamaAkaun, onPadamAkaun,

    /* Membuka pengurus template. Tanpa sekurang-kurangnya satu template,
       tiada hari yang mempunyai slot — jadi butang ini bukan hiasan, ia
       satu-satunya jalan keluar daripada kalendar yang kosong. */
    onUrusTemplate,

    /* ⭐ Butang "Show Control Panel" — dihantar oleh WorkspaceView SAHAJA
       (panel milik App.jsx). Ia muncul dalam bar kalendar bila panel
       tersembunyi; pada tab kalendar, butang di jalur tab disorok supaya
       tidak berganda (lihat WorkspaceView, syarat !schedMode). Prototaip tidak
       menghantar apa-apa → butang tidak muncul di lakaran. */
    onPaparPanel = null,
    panelSorok = false,

    /* ⭐ "Show list" — pasangan kepada pill "Hide list" dalam kepala senarai
       (arahan user 2026-11-01). Bila senarai produk tersorok, kepala (dengan
       pill Hide) tidak dirender langsung, jadi butang buka balik hidup DI SINI,
       dalam bar kalendar — sama tempat dengan "Show Control Panel". `senaraiKecut`
       = senarai sedang tersorok; `onPaparSenarai` = buka balik. WorkspaceV4
       sahaja menghantarnya; tanpa prop, tiada butang (classic kekal jalur nipis). */
    onPaparSenarai = null,
    senaraiKecut = false,

    /* ⭐ Elemen DOM dalam jalur tab (schedMode) tempat bar tarikh kalendar
       (Today · anak panah · julat) dipaparkan melalui portal — arahan user
       2026-08-21 "sebaris tab". Diteruskan bulat-bulat ke KalendarMingguV39;
       prototaip tidak menghantarnya → bar tarikh kekal inline. */
    slotNav = null,

    /* ⭐ v4 (suis generateV2, admin sahaja): diteruskan ke KalendarMingguV39
       supaya bar tarikh bertukar susunan ‹ · Today · › + "Bulan Tahun" besar.
       Lalai false = laluan v3.9 live tak berubah. */
    v4 = false,

    /* Slot kedua dalam jalur yang sama (hujung kanan) untuk baris akaun +
       Schedule template — arahan user 26 Okt. Diteruskan bulat-bulat ke
       KalendarMingguV39; tanpa slot, baris itu kekal di bawah bar tarikh. */
    slotBarKiri = null,

    /* Set yang sudah dijadualkan pada kalendar akaun LAIN:
         { [idProduk]: { set1: 'Akaun 1', set2: 'Akaun 1' } }

       ⛔ Ia MEMBERITAHU, bukan MENGHALANG (keputusan user 2026-08-13).
          Nama akaun ditaip bebas oleh pengguna, jadi sistem tidak tahu
          sama ada "Akaun 2" itu TikTok kedua (mengulang video di sana
          berisiko dikira kandungan berulang) atau Threads (platform
          lain — memang selamat dan memang dikehendaki). Yang tidak tahu
          tidak layak menyekat; ia hanya menandakan supaya pengulangan
          berlaku dengan mata terbuka. */
    setLuar = {},

    /* Kawalan susunan senarai produk. Ia dihantar masuk dan bukan dibina
       di sini kerana susunan itu milik pemanggil — dialah yang memegang
       senarai `kad` dan yang tahu bagaimana ia disusun.

       Ia duduk di atas senarai produk, bukan di jalur alat halaman
       (arahan user 2026-08-13): skrin ini ada DUA grid, dan kawalan yang
       melayang di atas kedua-duanya tidak memberitahu yang mana ia
       susun. */
    susunKawalan = null,

    /* ⭐ Workspace SEBENAR memasang senarai kirinya sendiri (kad penuh yang
       sama seperti tab Flow — keputusan user 2026-08-13), jadi di sana
       komponen ini hanya menyumbang kalendar. Prototaip tidak menghantar
       apa-apa dan kekal membawa kedua-dua lajur.

       ⛔ Jangan salin logik kalendar ke pemanggil untuk mengelak suis ini —
          `letak`/`tukarSet`/`buang` dan peraturan pemilihan set hidup di
          sini, dan dua salinan bermakna satu daripadanya akan terlepas
          pembetulan. */
    hanyaKalendar = false,

    /* Senarai produk PENUH — tanpa tapisan group, tab atau carian. Ia
       dipakai HANYA untuk memadankan slot kalendar dengan produknya.

       ⛔ Jangan gantikan dengan `kad`. Kalendar milik AKAUN, bukan milik
          group: video yang dijadualkan semasa berada dalam Group A mesti
          kekal kelihatan selepas pengguna beralih ke Group B. Menggunakan
          senarai tertapis di sini menyebabkan slot tersebut kelihatan
          kosong — dan seretan berikutnya menimpanya secara kekal. */
    kadSemua = null,

    /* Laporan selepas seretan PUKAL — { diletak, tinggal, hariAkhir }.
       Ia dihantar keluar dan bukan dipapar di sini kerana seretan pukal
       boleh mengisi hari yang tidak kelihatan pada tetingkap kalendar
       semasa; tanpa satu ayat yang memberitahu, pengguna akan menyangka
       separuh seretannya hilang. Prototaip tidak menghantar apa-apa. */
    onLapor,

    /* Lapor pilihan slot NAIK kepada pemanggil (WorkspaceView) — supaya butang
       Run di Control Panel tahu slot mana hendak dijalankan. Model SAMA seperti
       tab Flow: pilih kad di sini, Run di Control Panel. Prototaip tidak
       menghantar → tiada laporan, tiada kotak tick pilih-hari. */
    onPilihBerubah = null,

    /* Langkah 4: retry slot gagal. `bilGagal` = kiraan slot gagal MENTAH
       (pagar jalur atas); `onRetryGagal(off,minit)` = retry satu slot;
       `onRetrySemuaGagal(kunci[])` = retry slot yang disenaraikan.
       ★ [2026-09-18] Nombor yang DIPAPAR pada jalur kini datang daripada
       `useGagalDitutup` — slot gagal yang kelihatan dan belum ditutup (×). */
    onRetryGagal = null, bilGagal = 0, onRetrySemuaGagal = null,

    /* Stop: `bilSending` = slot 'running' ("Sending…"); `onStopLarian()` =
       hentikan + bersihkan yang tersangkut. */
    bilSending = 0, onStopLarian = null,

    /* Langkah 5: kawalan per-slot dalam popup butiran.
       `onTukarStatus(off,minit,st)` = tukar status secara manual (dropdown) —
       berguna bila robot silap tanda atau pengguna nak tandakan sendiri.
       `onPadamVideo(pid,setKey)` = padam video untuk produk+set slot itu
       (sama seperti "Del Video" dalam bar). Tanpa prop, kawalan tidak muncul. */
    onTukarStatus = null, onPadamVideo = null,

    /* ★ [2026-09-23] `onTukarStatusSlot(kunciSlot[], st, label)` — butang
       "Status" dalam bar pilihan slot (menggantikan "Kosongkan status" 16 Sept;
       arahan user: "buat macam di bar kad kiri"). `st` '' = kosongkan, iaitu
       aliran lama: Run TikTok → siap → kosongkan status → Run Facebook pada slot
       yang SAMA. Video KEKAL pada waktunya dalam semua keadaan — hanya tanda
       status berubah, dan pil pada kad di senarai kiri ikut sama.
       `namaAkaunStatus` = nama akaun yang dibuka, dipapar dalam kepala menu
       supaya skop tindakan sentiasa kelihatan. Tanpa prop = butang tidak muncul. */
    onTukarStatusSlot = null, namaAkaunStatus = '',
}) {
    const t = useT();
    const terkawal = typeof onUbahSusunan === 'function';
    const [dalaman, setDalaman] = useState(SUSUNAN_KOSONG);
    const [bukaMenuStatus, setBukaMenuStatus] = useState(false);

    const kini = (terkawal ? susunan : dalaman) || SUSUNAN_KOSONG;
    const hariTemplate = kini.hariTemplate || {};

    /* ---------- Isian MENTAH lawan isian PAPAR --------------------------
       ⛔ Bezakan kedua-duanya dengan tegas. Setiap TULISAN mesti bermula
          daripada `isianMentah`; setiap PAPARAN membaca `isian` di bawah.

       Sebabnya kerosakan data sebenar, bukan kekemasan: dahulu pemanggil
       menghantar isian yang sudah DITAPIS (produk group semasa sahaja),
       sedangkan setiap tulisan pergi ke objek mentah. Slot milik produk
       group lain kelihatan "kosong", lalu seretan pukal menimpanya —
       kekal. `undo` lebih teruk: ia menyimpan snapshot tertapis dan
       menulisnya semula ke atas data mentah, memadam setiap entri yang
       kebetulan tersembunyi pada saat itu.

       Sekarang pemanggil menghantar susunan MENTAH, dan padanan produk
       berlaku di sini — untuk paparan sahaja. */
    /* Diberi identiti yang STABIL. `kini.isian || {}` mencipta objek baharu
       setiap render apabila isian kosong, dan itu memaksa padanan produk di
       bawah dikira semula pada setiap render. */
    const isianMentah = useMemo(() => kini.isian || {}, [kini.isian]);
    /* Waktu yang pengguna tambah sendiri, per hari: { [hari]: [minit, …] }.
       Ia hidup dalam susunan (bukan dalam template) supaya template kekal
       peraturan yang boleh diguna semula. */
    const waktuTambahan = kini.waktuTambahan || {};

    /* Peta rujukan produk. `kadSemua` ialah senarai PENUH tanpa tapisan
       group/carian; `kad` (senarai kiri) memang ditapis dan tidak boleh
       dipakai di sini — itulah punca pepijat di atas. Prototaip tidak
       menghantar `kadSemua`, jadi ia jatuh balik kepada `kad`. */
    const petaKad = useMemo(
        () => new Map((kadSemua?.length ? kadSemua : kad).map((p) => [p.id, p])),
        [kadSemua, kad],
    );

    /* Isian untuk PAPARAN: setiap entri dipadankan dengan produk terkini.
       Entri yang produknya sudah benar-benar dipadam digugurkan di sini —
       bukan dipadam daripada simpanan, kerana membuang data semasa
       melukis skrin ialah cara paling senang kehilangan kerja pengguna
       akibat satu pepijat padanan. Pembersihan sebenar berlaku pada
       laluan padam produk. */
    const isian = useMemo(() => {
        const keluar = {};
        for (const k in isianMentah) {
            const e = isianMentah[k];
            /* `e.p?.id` ialah sokongan untuk data lama yang menyimpan
               SALINAN penuh objek produk. Bentuk baharu menyimpan rujukan
               `pid` sahaja. */
            const pid = e?.pid || e?.p?.id;
            const p = pid ? petaKad.get(pid) : null;
            if (!p || !e?.s) continue;
            /* ★ LANGKAH 5 — status per-slot. `st` (status post) + `sb` (sebab)
               disimpan pada ENTRI slot sendiri, BUKAN dibaca daripada produk.
               Sebabnya betul-betul menjawab aduan pengguna: dahulu status
               diambil daripada produk, jadi ia dikongsi antara slot yang guna
               produk sama DAN ditimpa setiap kali produk itu dijalankan semula
               — pengguna lupa slot mana yang sudah dijadualkan. Kini setiap
               slot ingat hasilnya sendiri dan ia kekal. */
            /* ★ [2026-11-03] `md` ('now' = label Posted) + `pf` (platform larian:
               'fb'/'sp'; tiada = TikTok) WAJIB ikut. Tanpa `md`, slot Post now
               berlabel "Scheduled" walaupun data betul (sejak 27 Sept); tanpa `pf`,
               pindahBanyak di bawah menggugurkan platform → Retry ke robot salah.
               ⚠️ Medan slot baharu = tambah DI SINI + pindahBanyak + jadualService. */
            keluar[k] = {
                pid, s: e.s, p, st: e.st || '', sb: e.sb || '',
                ...(e.md === 'now' ? { md: 'now' } : {}),
                ...(e.pf ? { pf: e.pf } : {}),
            };
        }
        return keluar;
    }, [isianMentah, petaKad]);

    /* ★ [2026-09-18] Butang × pada jalur "N slots failed" (arahan user).
       Menyorok jalur sahaja — status Failed kekal pada card + slot. Nota
       penuh dalam hooks/useGagalDitutup.js. Dibaca daripada isian PAPAR:
       slot yang produknya tiada tidak boleh dilihat atau dibersihkan, jadi
       ia tidak lagi membuat jalur melekat tanpa jalan keluar. */
    const { kunciTunjuk: gagalTunjuk, tutup: tutupGagal } = useGagalDitutup(akaunAktif, isian);

    /* Satu pintu untuk semua perubahan — sama ada ia pergi ke state
       dalaman atau naik kepada pemanggil. Bentuknya fungsi (prev → next)
       supaya dua perubahan berturut-turut tidak menimpa antara satu sama
       lain. */
    const tulis = (fn) => (terkawal ? onUbahSusunan(fn) : setDalaman((prev) => fn(prev || SUSUNAN_KOSONG)));

    /* ---------- Undo ---------------------------------------------------
       Satu seretan pukal kini boleh mengubah 18 slot sekali gus. Tanpa
       jalan balik, satu seretan tersilap bermakna menyusun semula
       segalanya dengan tangan.

       Yang disimpan ialah keadaan PENUH (hariTemplate + isian), bukan
       tampalan — menukar template hari turut mengosongkan isian hari itu,
       jadi undo yang hanya memulihkan isian akan memulihkan video ke atas
       waktu yang sudah tidak wujud lagi. */
    /* Tumpukan disimpan BERSAMA id akaun pemiliknya. Setiap akaun memegang
       kalendarnya sendiri, jadi langkah akaun lain tidak boleh dipulihkan ke
       sini — itu akan memindahkan video antara akaun tanpa diminta. Menyimpan
       pemiliknya di dalam state (dan bukan mengosongkannya melalui useEffect)
       bermakna pertukaran akaun membatalkan sejarah SERTA-MERTA pada render
       yang sama, tanpa satu pusingan render tambahan. */
    const [sejarah, setSejarah] = useState({ akaun: akaunAktif, tumpukan: [] });
    const tumpukanSah = sejarah.akaun === akaunAktif ? sejarah.tumpukan : [];
    const bolehUndo = tumpukanSah.length > 0;

    const ubah = (fn) => {
        setSejarah((s) => {
            const asal = s.akaun === akaunAktif ? s.tumpukan : [];
            /* ⚠️ `isianMentah`, BUKAN `isian`. Snapshot yang diambil daripada
               isian PAPAR akan memadam kekal setiap entri yang produknya
               kebetulan tidak kelihatan pada saat itu, sebaik Undo ditekan. */
            return { akaun: akaunAktif, tumpukan: [...asal, { hariTemplate, isian: isianMentah, waktuTambahan }].slice(-30) };
        });
        tulis(fn);
    };

    const undo = () => {
        const lalu = tumpukanSah[tumpukanSah.length - 1];
        if (!lalu) return;
        setSejarah({ akaun: akaunAktif, tumpukan: tumpukanSah.slice(0, -1) });
        tulis((prev) => ({ ...prev, hariTemplate: lalu.hariTemplate, isian: lalu.isian, waktuTambahan: lalu.waktuTambahan || {} }));
    };

    /* ---------- Tanda pilihan slot ------------------------------------
       Disimpan bersama id akaun atas sebab yang sama seperti sejarah undo.
       Ia turut ditapis kepada slot yang MASIH wujud: undo, padam dan pindah
       boleh melenyapkan slot yang sedang bertanda, dan pilihan yang menunjuk
       kepada slot hantu akan menjadikan kiraan "5 dipilih" bohong. */
    const [pilihState, setPilihState] = useState({ akaun: akaunAktif, kunci: [] });
    const pilihSah = new Set(
        (pilihState.akaun === akaunAktif ? pilihState.kunci : []).filter((k) => isian[k]),
    );
    const setPilih = (set) => setPilihState({ akaun: akaunAktif, kunci: [...set] });
    const kosongkanPilihan = () => setPilihState({ akaun: akaunAktif, kunci: [] });
    /* Pilih SEMUA slot berisi (akaun ini) — butang dalam bar pukal. */
    const pilihSemua = () => setPilih(new Set(Object.keys(isian)));

    /* ★ Lapor pilihan NAIK ke WorkspaceView (cermin). `pilihSig` ialah tanda
       tangan stabil (kunci disusun) supaya effect hanya berdenyut bila pilihan
       BENAR-BENAR berubah, bukan setiap render. Butang Run di Control Panel
       membaca cermin ini — sama seperti pilih kad di tab Flow. */
    const pilihSig = [...pilihSah].sort().join('|');
    useEffect(() => {
        onPilihBerubah?.(pilihSig ? pilihSig.split('|') : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pilihSig]);

    const adaVideo = kad.filter((p) => (p.videos || []).some((v) => v === 1));
    const semuaIsi = Object.values(isian);

    const setDiguna = (idProduk) =>
        semuaIsi.filter((v) => v.p.id === idProduk).map((v) => v.s);

    const pilihTemplate = (hari, idTemplate) => {
        ubah((prev) => {
            /* Menukar template mengubah waktu yang wujud pada hari itu, jadi
               slot yang sudah diisi mungkin tidak lagi ada padanan waktunya.
               Membiarkannya bermakna video "hilang" tanpa jejak — jadi isian
               hari itu dikosongkan bersama. */
            const isiBaru = {};
            for (const k in (prev.isian || {})) {
                if (!k.startsWith(`${hari}_`)) isiBaru[k] = prev.isian[k];
            }
            /* ⚠️ `...prev` WAJIB. Objek ini pernah dibina dari awal, dan itu
                  MEMADAM `waktuTambahan` secara senyap: pengguna menambah
                  waktunya sendiri, kemudian menukar template pada hari yang
                  sama, dan waktu itu lenyap tanpa sebarang mesej. Waktu
                  tambahan milik HARI, bukan milik template — menukar template
                  tidak sepatutnya menyentuhnya langsung. */
            return {
                ...prev,
                hariTemplate: { ...(prev.hariTemplate || {}), [hari]: idTemplate },
                isian: isiBaru,
            };
        });
    };

    /* ---------- Seretan PUKAL ----------------------------------------
       Muatan seretan ialah senarai id dipisah koma. Satu id = laluan lama
       di bawah (satu slot, satu set). Lebih daripada satu = laluan ini.

       Bilangan slot ditentukan TEMPLATE hari yang diseret, bukan bilangan
       kad (keputusan user 2026-08-13) — template itulah yang menyatakan
       set mana hendak dipost. Tiga kad × "Semua set" = sembilan video.

       Slot yang tidak muat pada hari itu mengalir ke hari berikutnya yang
       ADA template. Ini bukan kemewahan: pengguna yang menanda 20 kad
       memang tidak boleh dijangka mengira sendiri berapa yang muat pada
       satu hari, dan itulah maksud "satu klik borong". */
    const letakPukal = (ids, hari, minit) => {
        const t = templates.find((x) => x.id === hariTemplate[hari]);
        /* ★ Pepijat 2: hari tanpa template tiada kotak slot langsung, jadi
           seretan borong ke situ dulu GAGAL SENYAP. Lapor supaya pengguna
           tahu perlu pilih template dahulu, bukan tertanya-tanya kenapa
           tiada apa berlaku. */
        if (!t) { onLapor?.({ diletak: 0, tinggal: ids.length, tiadaTemplate: true }); return; }

        const kadTerpilih = ids.map((id) => kad.find((x) => x.id === id)).filter(Boolean);
        if (!kadTerpilih.length) return;

        /* Set yang sudah ada dalam kalendar INI, ikut produk. Tanpa ini,
           seretan pukal kedua akan menimbun pendua di atas yang pertama. */
        const guna = {};
        semuaIsi.forEach((v) => {
            if (!v?.p?.id || !v.s) return;
            (guna[v.p.id] = guna[v.p.id] || new Set()).add(v.s);
        });

        const video = kembangIkutTemplate(kadTerpilih, t, guna);
        const slot = slotKosongDari(hari, minit, { templates, hariTemplate, isian, waktuTambahan });
        const muat = Math.min(video.length, slot.length);

        if (!muat) {
            onLapor?.({ diletak: 0, tinggal: video.length });
            return;
        }

        ubah((prev) => {
            const isiBaru = { ...(prev.isian || {}) };
            for (let i = 0; i < muat; i++) {
                /* RUJUKAN sahaja. Menyimpan salinan penuh objek produk di sini
                   membawa `prompts` (puluhan KB) ke dalam setiap slot, dan
                   salinan itu jadi basi sebaik produk diedit. */
                isiBaru[kunciSlot(slot[i].hari, slot[i].minit)] = { pid: video[i].p.id, s: video[i].s };
            }
            return { ...prev, isian: isiBaru };
        });

        onLapor?.({ diletak: muat, tinggal: video.length - muat, hariAkhir: slot[muat - 1].hari });
    };

    const letak = (muatan, hari, minit) => {
        const ids = String(muatan || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (ids.length > 1) return letakPukal(ids, hari, minit);

        const p = kad.find((x) => x.id === ids[0]);
        if (!p) return;

        /* Set lalai — dipilih mengikut EMPAT keutamaan:
             1. ada video · belum dipakai di sini · belum dipakai akaun lain
             2. ada video · belum dipakai di sini (walaupun akaun lain ada)
             3. BELUM ada video · belum dipakai di sini → slot menunggu videonya
             4. apa-apa yang ada video

           Keutamaan pertama itulah yang menjawab kes sebenar pengguna:
           set 1 dan 2 sudah masuk kalendar Akaun 1, jadi seretan ke Akaun 2
           terus jatuh pada set 3 tanpa dia perlu memilihnya sendiri.
           Keutamaan kedua dan keempat menjaga janji "menandakan, bukan
           menghalang" — seretan tidak pernah ditolak sunyi.

           ⭐ Keutamaan KETIGA ditambah 2026-08-13 (keputusan user): produk
              ditanda Schedule SEBELUM Flow dijalankan, jadi slot mesti boleh
              ditempah dahulu dan menunggu videonya. Ia sengaja duduk di bawah
              (1) dan (2): set yang videonya sudah siap patut dihabiskan
              dahulu, kalau tidak slot yang boleh terus dipakai ditinggalkan
              kosong sedangkan slot menunggu bertambah. */
        const guna = setDiguna(p.id);
        const luar = setLuar[p.id] || {};
        const semua = ['set1', 'set2', 'set3'];
        const ada = semua.filter((_, i) => (p.videos || [])[i] === 1);
        const bebas = ada.filter((x) => !guna.includes(x));
        const bebasTanpaVideo = semua.filter((x) => !guna.includes(x) && !ada.includes(x));
        const s = bebas.find((x) => !luar[x]) || bebas[0] || bebasTanpaVideo[0] || ada[0];
        /* Hanya berlaku bila ketiga-tiga set produk ini SUDAH ada dalam
           kalendar ini — tiada apa lagi untuk dijatuhkan. */
        if (!s) return;

        /* ★ Pepijat 1: JANGAN timpa slot yang sudah berisi dengan kad BARU.
           Seret kad sedia ada = bertukar tempat (lihat `pindah`), tapi kad
           baru dari senarai tiada sumber untuk ditukar — dulu ia menimpa
           slot lama tanpa amaran (kerja pengguna hilang). Sekarang: kalau
           slot sasaran berisi, letak pada slot KOSONG terdekat dari titik
           itu; kalau tiada langsung, lapor dan jangan hilangkan apa-apa. */
        let hariT = hari;
        let minitT = minit;
        if (isian[kunciSlot(hari, minit)]) {
            const kosong = slotKosongDari(hari, minit, { templates, hariTemplate, isian, waktuTambahan });
            if (!kosong.length) { onLapor?.({ diletak: 0, tinggal: 1 }); return; }
            hariT = kosong[0].hari;
            minitT = kosong[0].minit;
        }

        ubah((prev) => ({
            ...prev,
            isian: { ...(prev.isian || {}), [kunciSlot(hariT, minitT)]: { pid: p.id, s } },
        }));
    };

    /* ★ Letak SATU set tertentu (arahan user 2026-08-21) — pengguna menyeret
       kotak video set itu terus ke slot. Beza dengan `letak`: set TIDAK dipilih
       automatik, ia yang pengguna seret. Semantik ALIH: kalau set itu sudah ada
       dalam kalendar ini, ia BERPINDAH ke slot baharu (bukan pendua) — satu set,
       satu masa. */
    const letakSet = (pid, setKey, hari, minit) => {
        const p = kad.find((x) => x.id === pid);
        if (!p || !setKey) return;

        let hariT = hari;
        let minitT = minit;
        const isiSasar = isian[kunciSlot(hari, minit)];
        const sama = isiSasar && isiSasar.pid === pid && isiSasar.s === setKey;
        if (isiSasar && !sama) {
            const kosong = slotKosongDari(hari, minit, { templates, hariTemplate, isian, waktuTambahan });
            if (!kosong.length) { onLapor?.({ diletak: 0, tinggal: 1 }); return; }
            hariT = kosong[0].hari;
            minitT = kosong[0].minit;
        }

        ubah((prev) => {
            const isi = { ...(prev.isian || {}) };
            /* Buang kemunculan lama set ini (alih, bukan pendua). */
            for (const k in isi) {
                if (isi[k] && isi[k].pid === pid && isi[k].s === setKey) delete isi[k];
            }
            isi[kunciSlot(hariT, minitT)] = { pid, s: setKey };
            return { ...prev, isian: isi };
        });
    };

    /* ---------- Pindah / bertukar tempat ------------------------------
       Slot yang sudah terisi boleh diseret ke waktu lain, termasuk merentas
       hari. Setnya TIDAK dipilih semula — pengguna sudah membuat keputusan
       itu, dan memilihnya semula bermakna S3 yang dia letak dengan sengaja
       boleh bertukar menjadi S1 hanya kerana dia mengalihkan waktunya.

       Jatuh atas slot yang sudah berisi = DUA-DUA bertukar tempat. Menimpa
       akan memadamkan kerja pengguna tanpa amaran, dan menolaknya pula
       memaksa dia mengosongkan slot dahulu — bertukar tempat ialah satu-
       satunya hasil yang tidak pernah menghilangkan apa-apa. */
    const pindah = (dariKunci, hari, minit) => {
        const keKunci = kunciSlot(hari, minit);
        if (!dariKunci || dariKunci === keKunci) return;

        ubah((prev) => {
            const isi = { ...(prev.isian || {}) };
            const asal = isi[dariKunci];
            if (!asal) return prev;          // slot sumber sudah tiada (dipadam semasa seretan)

            const sasaran = isi[keKunci];
            isi[keKunci] = asal;
            if (sasaran) isi[dariKunci] = sasaran;
            else delete isi[dariKunci];
            return { ...prev, isian: isi };
        });
    };

    /* ---------- Pindah PUKAL antara slot ------------------------------
       Slot bertanda diseret sekali gus. Turutan waktu asal dikekalkan —
       susunan yang pengguna bina (S1 pagi, S2 tengah hari, S3 petang)
       ialah keputusannya, bukan kebetulan.

       Slot sumber dikosongkan DAHULU sebelum destinasi dikira, supaya
       menggeser satu blok ke atas dalam hari yang sama boleh menggunakan
       semula slot yang baru sahaja dikosongkan sendiri. */
    const pindahBanyak = (senaraiKunci, hari, minit) => {
        const entri = senaraiKunci
            .filter((k) => isian[k])
            .map((k) => {
                const [h, m] = k.split('_').map(Number);
                /* Disimpan sebagai RUJUKAN, bukan entri papar — `isian[k]`
                   membawa objek produk yang dipadankan untuk melukis skrin,
                   dan menulisnya semula ke simpanan akan mengembalikan
                   salinan penuh yang baru sahaja dibuang. */
                const { p: _p, ...rujukan } = isian[k];   // md + pf ikut (lihat isian di atas)
                return { k, v: rujukan, h, m };
            })
            .sort((a, b) => a.h - b.h || a.m - b.m);
        if (!entri.length) return;

        const isiTanpaSumber = { ...isian };
        entri.forEach((x) => { delete isiTanpaSumber[x.k]; });

        const slot = slotKosongDari(hari, minit, { templates, hariTemplate, isian: isiTanpaSumber, waktuTambahan });
        const muat = Math.min(entri.length, slot.length);
        if (!muat) {
            onLapor?.({ diletak: 0, tinggal: entri.length });
            return;
        }

        ubah((prev) => {
            const isi = { ...(prev.isian || {}) };
            entri.forEach((x) => { delete isi[x.k]; });
            for (let i = 0; i < muat; i++) isi[kunciSlot(slot[i].hari, slot[i].minit)] = entri[i].v;
            /* Baki DIKEMBALIKAN ke slot asalnya. Slot destinasi yang tidak
               cukup bukan alasan untuk video hilang. */
            for (let i = muat; i < entri.length; i++) isi[entri[i].k] = entri[i].v;
            return { ...prev, isian: isi };
        });

        kosongkanPilihan();
        onLapor?.({ diletak: muat, tinggal: entri.length - muat, hariAkhir: slot[muat - 1].hari });
    };

    /* ---------- Waktu tambahan (hari itu sahaja) ---------------------- */
    const tambahWaktu = (hari, minit) => {
        ubah((prev) => {
            const semua = prev.waktuTambahan || {};
            const hariIni = semua[hari] || [];
            if (hariIni.includes(minit)) return prev;
            return { ...prev, waktuTambahan: { ...semua, [hari]: [...hariIni, minit].sort((a, b) => a - b) } };
        });
    };

    const buangWaktu = (hari, minit) => {
        ubah((prev) => {
            const semua = prev.waktuTambahan || {};
            const baki = (semua[hari] || []).filter((m) => m !== minit);
            const next = { ...semua };
            if (baki.length) next[hari] = baki; else delete next[hari];
            /* Slot itu dijamin kosong (butang buang hanya wujud pada slot
               kosong), jadi tiada isian yang perlu dibersihkan di sini. */
            return { ...prev, waktuTambahan: next };
        });
    };

    const buangPilihan = () => {
        if (!pilihSah.size) return;
        ubah((prev) => {
            const isi = { ...(prev.isian || {}) };
            pilihSah.forEach((k) => { delete isi[k]; });
            return { ...prev, isian: isi };
        });
        kosongkanPilihan();
    };

    const tukarSet = (hari, minit, s) => {
        ubah((prev) => {
            const k = kunciSlot(hari, minit);
            if (!prev.isian?.[k]) return prev;
            /* Tukar set = set BERBEZA → statusnya juga berbeza. Mengekalkan
               status lama (cth 'complete' milik S1) pada S2 akan menipu, jadi
               st/sb DIBUANG — slot kembali "belum dijalankan" untuk set baharu. */
            return { ...prev, isian: { ...prev.isian, [k]: { pid: prev.isian[k].pid, s } } };
        });
    };

    const buang = (hari, minit) => {
        ubah((prev) => {
            const k = kunciSlot(hari, minit);
            if (!prev.isian?.[k]) return prev;
            const isiBaru = { ...prev.isian };
            delete isiBaru[k];
            return { ...prev, isian: isiBaru };
        });
    };

    /* Penukar akaun dan butang template duduk di dalam bar navigasi
       kalendar, bukan pada baris sendiri — bar itu sudah menolak
       navigasinya ke kanan, jadi bahagian kirinya memang kosong.

       Turutan mengikut soalan yang pengguna tanya pada dirinya: kalendar
       SIAPA ini → apa peraturan masanya → apa yang ditanda.

       ⚠️ TIADA butang "kosongkan semua kalendar" di sini — SENGAJA dibuang
          (arahan user 2026-08-20). Satu klik memadam SEMUA slot tanpa
          pengesahan terlalu mudah tersilap tekan. Pengguna mengosongkan
          per HARI (kepala hari) atau per SLOT (pangkah pada slot); Undo
          kekal sebagai jaring untuk seretan pukal. Jangan pulangkan butang
          ini tanpa sekurang-kurangnya satu langkah pengesahan. */
    /* ⭐ Bar navigasi kalendar DUA baris (arahan user 2026-08-21):
       (1) Today + anak panah + tarikh; (2) penukar akaun + Schedule
       template (+ Undo) di KIRI, Show Control Panel ditolak ke HUJUNG
       KANAN baris yang SAMA. `barKiri` = seluruh baris 2 (lebar penuh);
       KalendarMingguV39 menyusunnya di bawah baris tarikh. */
    const barKiri = (akaun || onUrusTemplate || bolehUndo || (panelSorok && onPaparPanel) || (senaraiKecut && onPaparSenarai)) ? (
        <span style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* ⭐ "Show list" di HUJUNG KIRI (arahan user 2026-11-01) — pasangan
                pill "Hide list" dalam kepala senarai. Teks penuh (bukan ikon
                sahaja — "org tak faham" ikon), gaya SAMA dengan Show Control
                Panel. Muncul hanya bila senarai tersorok. */}
            {senaraiKecut && onPaparSenarai && (
                <Btn
                    variant="ghost" size="sm"
                    onClick={onPaparSenarai}
                    icon={<PanelLeftOpen size={14} strokeWidth={2} />}
                >
                    Show list
                </Btn>
            )}
            {akaun && (
                <PenukarAkaun
                    senarai={akaun}
                    aktif={akaunAktif}
                    onTukar={onTukarAkaun}
                    onCipta={onCiptaAkaun}
                    onNama={onNamaAkaun}
                    onPadam={onPadamAkaun}
                />
            )}
            {onUrusTemplate && (
                <Btn
                    variant="ghost" size="sm"
                    onClick={onUrusTemplate}
                    icon={<CalendarClock size={14} strokeWidth={2} />}
                >
                    {t('workspace.tiktok.scheduleTemplate')}
                </Btn>
            )}
            {/* ⛔ Tindakan pukal slot (Buang/Batal) DIPINDAH ke BAR PUKAL gaya
                Flow di atas kalendar (`barPilih` di bawah) — arahan user
                2026-08-20. Bar pukal itu SENGAJA tiada butang Run: butang Run
                kekal di Control Panel dan menjalankan slot yang DIPILIH, sama
                seperti pilih kad di tab Flow. Jangan pulangkan tindakan pilih ke
                jalur navigasi ini. */}
            {/* Undo mendahului Clear: ia langkah pemulihan, dan tempatnya
                sebelum butang yang paling kerap perlu dipulihkan. */}
            {bolehUndo && (
                <Btn
                    variant="quiet" size="sm"
                    onClick={undo}
                    icon={<Undo2 size={13} strokeWidth={2} />}
                    title={t('workspace.tiktok.undoTitle')}
                >
                    {t('workspace.tiktok.undo')}
                </Btn>
            )}
            {/* ⭐ Show Control Panel BERTEKS (bukan ikon — "org tak faham"
                ikon; arahan user 2026-08-20) di HUJUNG KANAN baris 2 (arahan
                user 2026-08-21) — `marginLeft:auto` menolaknya ke kanan.
                Muncul hanya bila panel tersembunyi; ia menggantikan butang
                di jalur tab pada tab ini. */}
            {panelSorok && onPaparPanel && (
                <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                    <Btn
                        variant="ghost" size="sm"
                        onClick={onPaparPanel}
                        icon={<PanelRightOpen size={14} strokeWidth={2} />}
                    >
                        {t('ws.panel.paparButang')}
                    </Btn>
                </span>
            )}
        </span>
    ) : null;

    const kalendar = (
        <KalendarMingguV39
            templates={templates}
            hariTemplate={hariTemplate}
            onPilihTemplate={pilihTemplate}
            isian={isian}
            onLetak={letak}
            onLetakSet={letakSet}
            onPindah={pindah}
            onPindahBanyak={pindahBanyak}
            pilih={pilihSah}
            onPilih={setPilih}
            waktuTambahan={waktuTambahan}
            onTambahWaktu={tambahWaktu}
            onBuangWaktu={buangWaktu}
            onTukarSet={tukarSet}
            onBuang={buang}
            capsyen={capsyen}
            barKiri={barKiri}
            slotNav={slotNav}
            v4={v4}
            slotBarKiri={slotBarKiri}
            setLuar={setLuar}
            onRetryGagal={onRetryGagal}
            onTukarStatus={onTukarStatus}
            onPadamVideo={onPadamVideo}
        />
    );

    /* ★ LANGKAH 4 — jalur "N slot gagal" + Retry all (bulk-first). Muncul
       hanya bila ada slot GAGAL sebenar (bukan lagi data DEMO). Butiran
       setiap kegagalan ditanda pada slotnya sendiri di dalam kalendar;
       jalur ini hanya membawa kiraan + satu tindakan borong. Slot 'uncertain'
       (kuning) TIDAK dikira di sini — lihat schedSlotGagal.
       ★ [2026-09-18] × menyorok jalur ini (status kekal pada card + slot).
       Kiraan DAN Retry all hanya meliputi kegagalan yang belum ditutup —
       Retry all menghantar senarainya sendiri, jadi kegagalan yang pengguna
       sudah tutup tidak diulang tanpa dia sedar. */
    const barGagal = (bilGagal > 0 && gagalTunjuk.length > 0 && onRetrySemuaGagal) ? (
        <div className="s-bargagal">
            <div className="s-bargagal__atas">
                <span className="s-bargagal__tajuk">
                    <AlertTriangle size={14} strokeWidth={2} aria-hidden="true" />
                    {t('workspace.tiktok.gagalBar', { n: gagalTunjuk.length })}
                </span>
                <button type="button" className="s-bargagal__semua" onClick={() => onRetrySemuaGagal(gagalTunjuk)}>
                    <RefreshCw size={13} strokeWidth={2} aria-hidden="true" />
                    {t('workspace.tiktok.retryAll')}
                </button>
                <button
                    type="button" className="s-bargagal__tutup" onClick={tutupGagal}
                    title={t('workspace.tiktok.gagalTutup')} aria-label={t('workspace.tiktok.gagalTutup')}
                >
                    <X size={14} strokeWidth={2.2} aria-hidden="true" />
                </button>
            </div>
        </div>
    ) : null;

    /* ★ Jalur "N sedang dihantar" + Stop. Muncul bila ada slot 'running'
       ("Sending…"). Stop = hentikan larian + bersihkan slot yang tersangkut. */
    const barSending = (bilSending > 0 && onStopLarian) ? (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            padding: '8px 12px', marginBottom: 10, borderRadius: 10,
            background: 'var(--s-fill)', border: '1px solid var(--s-line-strong)',
        }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--s-brand)' }}>
                {t('workspace.tiktok.sendBar', { n: bilSending })}
            </span>
            <button type="button" className="s-bargagal__semua" onClick={onStopLarian}>
                <X size={13} strokeWidth={2.4} aria-hidden="true" />
                {t('workspace.tiktok.stopRun')}
            </button>
        </div>
    ) : null;

    /* ★ BAR PUKAL SLOT (arahan user 2026-08-20) — muncul bila ada slot dipilih,
       gaya SAMA dengan bar pilih kad di tab Flow (`.sesaat-barpukal-v39`,
       permukaan terbalik). ⛔ SENGAJA TIADA butang Run: butang Run kekal di
       Control Panel dan menjalankan slot yang dipilih (sama seperti pilih kad
       di tab Flow — bar pilih di sana pun tiada butang Run). Tindakan: Pilih
       semua · Buang (keluarkan slot dari kalendar, boleh Undo) · Status (tukar
       atau kosongkan — slot KEKAL, hanya tandanya berubah) · Batal. */
    const gayaBtnPukal = {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit', border: '1px solid transparent',
    };
    const barPilih = pilihSah.size > 0 ? (
        <div className="sesaat-barpukal-v39" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="sesaat-barpukal-kira">{t('workspace.tiktok.slotsSelected', { n: pilihSah.size })}</span>
            <span className="sesaat-barpukal-garis" />
            <button type="button" className="s-barpukal-act" style={gayaBtnPukal} onClick={pilihSemua}>
                {t('ws.pukal.pilihSemua')}
            </button>
            <button type="button" className="s-barpukal-act" style={gayaBtnPukal} onClick={buangPilihan} title={t('workspace.tiktok.removeSelectedTitle')}>
                <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
                {t('workspace.tiktok.remove')}
            </button>
            {onTukarStatusSlot && (
                <MenuStatusPukal
                    mod="slot"
                    buka={bukaMenuStatus}
                    onTogol={() => setBukaMenuStatus((v) => !v)}
                    onTutup={() => setBukaMenuStatus(false)}
                    namaAkaun={namaAkaunStatus}
                    bilSlot={pilihSah.size}
                    tajukButang={t('workspace.tiktok.statusSlotTitle')}
                    onPilih={(_set, st, label) => {
                        setBukaMenuStatus(false);
                        onTukarStatusSlot(Array.from(pilihSah), st, label);
                    }}
                    T={(kunci, lalai, vars) => t(kunci, vars) || lalai}
                />
            )}
            <span style={{ flex: 1 }} />
            <button type="button" className="s-barpukal-act" style={gayaBtnPukal} onClick={kosongkanPilihan} title={t('workspace.tiktok.deselectTitle')}>
                {t('workspace.tiktok.deselect')}
            </button>
        </div>
    ) : null;

    /* Lajur kiri milik pemanggil — pulangkan kalendar sahaja, tanpa grid.
       Grid di sana dibina oleh pemanggil kerana dialah yang memegang senarai
       kad penuh itu.

       Baki gagal dibawa sekali dalam KEDUA-DUA laluan pulangan. Kalau ia
       hanya dipasang pada laluan penuh di bawah, workspace sebenar (yang
       sentiasa memakai `hanyaKalendar`) tidak akan melihatnya langsung —
       dan di situlah ia paling perlu dinilai. */
    /* ⭐ Jalur pukal/status DISUNTIK KE DALAM kalendar, di bawah bar
       navigasinya — bukan dirender sebelum kalendar (arahan user 2026-08-21).

       Sebabnya penjajaran: lajur kiri bermula dengan baris kepala
       (Products N + kawalan susun), dan bar pukal kadnya duduk di BAWAH
       baris itu. Kalau jalur di sebelah kanan dirender sebelum bar navigasi
       kalendar, ia bermula lebih tinggi daripada bar kiri dan kedua-duanya
       nampak tidak sebaris — dan lebih teruk, seluruh kalendar teranjak ke
       bawah setiap kali satu slot ditanda. */
    const barAtasGabung = (barPilih || barSending || barGagal)
        ? (<>{barPilih}{barSending}{barGagal}</>)
        : null;
    const kalendarLengkap = cloneElement(kalendar, { barAtas: barAtasGabung });

    if (hanyaKalendar) return kalendarLengkap;

    return (
        <div className="s-tikjad">

            {/* ---------- KIRI · senarai produk ---------- */}
            <div>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 14, flexWrap: 'wrap',
                }}>
                    {/* Terma English (arahan user 2026-08-09) — sama seperti
                        "Set", "Caption", "Schedule template" di sekelilingnya. */}
                    <span style={{ fontSize: 13, color: 'var(--s-ink)' }}>{t('workspace.tiktok.products')}</span>
                    <Badge tone="neutral">{adaVideo.length}</Badge>
                    {susunKawalan && (
                        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                            {susunKawalan}
                        </span>
                    )}
                    {/* ⛔ Butang "Clear" DIPINDAHKAN ke bar kalendar (aduan user
                        2026-08-13). Ia mengosongkan KALENDAR, bukan senarai
                        produk — tetapi berdiri di kepala senarai produk, ia
                        terbaca sebagai "buang produk-produk ini". Butang
                        hendaklah duduk atas benda yang ia ubah.
                        Jangan kembalikan ke sini. */}
                </div>

                {adaVideo.length === 0 ? (
                    <EmptyState title={t('workspace.tiktok.emptyTitle')}>
                        {t('workspace.tiktok.emptyBody')}
                    </EmptyState>
                ) : (
                    <div className="s-tikjad__senarai">
                        {adaVideo.map((p) => {
                            const jumlahSet = (p.videos || []).filter((v) => v === 1).length;
                            const sudah = setDiguna(p.id);
                            return (
                                <KadSeret
                                    key={p.id}
                                    p={p}
                                    jumlahSet={jumlahSet}
                                    sudah={sudah}
                                    /* Kad yang semua setnya dipakai akaun LAIN kekal boleh
                                       diseret — hanya set yang sudah masuk kalendar INI
                                       yang menghabiskannya. */
                                    boleh={sudah.length < jumlahSet}
                                    luar={setLuar[p.id]}
                                />
                            );
                        })}
                    </div>
                )}

                <p className="s-hint" style={{ marginTop: 10 }}>
                    {t('workspace.tiktok.hint')}
                </p>
            </div>

            {/* ---------- KANAN · kalendar ---------- */}
            <div style={{ minWidth: 0 }}>
                {barPilih}
                {barSending}
                {barGagal}
                {kalendarLengkap}
            </div>
        </div>
    );
}
